import type { CardRecord, Decision, NormalizedStatement, RecurringResponse, RowProblem, StatementSummary } from "@paredown/shared";

export type { RecurringResponse };

/** The API base URL is injected at build time by the deploy workflow. */
export const apiBase = import.meta.env.VITE_API_URL ?? "";

export interface StatementDetail extends Omit<StatementSummary, "issuer" | "accountToken" | "periodStart" | "periodEnd"> {
  redactedText: string;
  tokens: Record<string, string>;
  pageCount: number;
  extracted?: NormalizedStatement;
  confirmed?: NormalizedStatement;
  problems?: { rows: RowProblem[]; reconcile: string[] };
  /** Null when the PDF is no longer stored; the extracted rows are still there. */
  pdfUrl: string | null;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export function makeApi(getToken: () => string | undefined) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = getToken();
    const res = await fetch(`${apiBase}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { "content-type": "application/json" } : {}),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.status === 204) return undefined as T;
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) throw new ApiError(res.status, data.error ?? `HTTP ${res.status}`);
    return data as T;
  }
  return {
    listStatements: () => call<StatementSummary[]>("GET", "/statements"),
    getStatement: (id: string) => call<StatementDetail>("GET", `/statements/${id}`),
    uploadUrl: (fileName: string) =>
      call<{ key: string; url: string }>("POST", "/statements/upload-url", { fileName }),
    register: (body: unknown) => call<StatementSummary>("POST", "/statements", body),
    confirm: (id: string, statement: NormalizedStatement) =>
      call<StatementSummary>("POST", `/statements/${id}/confirm`, { statement }),
    retry: (id: string) => call<StatementSummary>("POST", `/statements/${id}/retry`),
    remove: (id: string) => call<void>("DELETE", `/statements/${id}`),
    confirmClean: () => call<{ confirmed: number }>("POST", "/statements/confirm-clean"),
    recurring: () => call<RecurringResponse>("GET", "/recurring"),
    decide: (key: string, decision: Decision | null) =>
      call<{ key: string; decision: Decision | null; card?: CardRecord }>("POST", "/recurring/decisions", { key, decision }),
    suggest: () => call<{ status: string }>("POST", "/recurring/suggest"),
    regenerateCard: (key: string) => call<{ status: string }>("POST", "/recurring/cards", { key }),
  };
}
export type Api = ReturnType<typeof makeApi>;

export async function putToS3(url: string, bytes: Uint8Array): Promise<void> {
  const res = await fetch(url, { method: "PUT", headers: { "content-type": "application/pdf" }, body: bytes as BodyInit });
  if (!res.ok) throw new Error(`upload failed: HTTP ${res.status}`);
}

export const money = (cents: number) =>
  (cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" });
